import React, { useRef, useEffect, useState } from 'react';
import { useWallet } from '../contexts/WalletContext';
import { useEscrow } from '../contexts/EscrowContext';
import jazzicon from '@metamask/jazzicon';
import ThemeToggle from './ThemeToggle';
import NetworkSelector from './NetworkSelector';
import WalletSelector from './WalletSelector';
import './Header.css';

interface HeaderProps {
  subtitle?: string;
}

const Header: React.FC<HeaderProps> = ({ subtitle }) => {
  const { isConnected, account, connectWalletWithProvider, disconnectWallet, error, loading, signer } = useWallet();
  const { contractAddress, clearContractAddress } = useEscrow();
  const iconRef = useRef<HTMLDivElement>(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const [isWalletSelectorOpen, setIsWalletSelectorOpen] = useState(false);

  const handleWalletAction = () => {
    if (isConnected) {
      disconnectWallet();
    } else {
      setIsWalletSelectorOpen(true);
    }
  };

  const handleWalletSelect = async (provider: any, providerName: string) => {
    try {
      await connectWalletWithProvider(provider, providerName);
    } catch (err) {
      console.error('Failed to connect with selected provider:', err);
    }
  };

  const handleSwitchEscrow = () => {
    clearContractAddress();
  };


  const toggleHeader = () => {
    setIsHeaderExpanded(!isHeaderExpanded);
  };

  useEffect(() => {
    if (account && iconRef.current) {
      // Clear previous icon
      iconRef.current.innerHTML = '';
      // Generate new jazzicon (account is always a valid hex address from wallet)
      const seed = parseInt(account.slice(2, 10), 16);
      if (!isNaN(seed)) {
        const icon = jazzicon(24, seed);
        iconRef.current.appendChild(icon);
      }
    }
  }, [account]);

  return (
    <header>
      <div className="header-content">
        <div className="header-title-section">
          <h1>
            trustline{subtitle && ` - ${subtitle}`}
          </h1>
          <button onClick={toggleHeader} className="header-toggle-btn" aria-label="Toggle header">
            <span className="desktop-arrow">{isHeaderExpanded ? '◄' : '►'}</span>
            <span className="mobile-arrow">{isHeaderExpanded ? '▲' : '▼'}</span>
          </button>
        </div>
        <div className={`wallet-section ${isHeaderExpanded ? 'expanded' : 'collapsed'}`}>
          {isConnected ? (
            <div className="wallet-info">
              {contractAddress && (
                <div className="contract-info">
                  <span className="contract-label">Contract:</span>
                  <span className="contract-address">{contractAddress.slice(0, 6)}...{contractAddress.slice(-4)}</span>
                  <button onClick={handleSwitchEscrow} className="switch-escrow-btn" title="Switch to another contract">
                    ↻
                  </button>
                </div>
              )}
              {signer && (
                <NetworkSelector />
              )}
              <div className="account-display">
                <div ref={iconRef} className="account-icon"></div>
                <span className="account">{account.slice(0, 6)}...{account.slice(-4)}</span>
              </div>
              <button onClick={handleWalletAction} className="wallet-btn disconnect">
                Disconnect
              </button>
            </div>
          ) : (
            <button 
              onClick={handleWalletAction} 
              className="wallet-btn connect"
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Connect Wallet'}
            </button>
          )}
          <ThemeToggle />
        </div>
      </div>
      {error && (
        <div className="header-error">
          {error}
        </div>
      )}
      
      <WalletSelector
        isOpen={isWalletSelectorOpen}
        onClose={() => setIsWalletSelectorOpen(false)}
        onSelectWallet={handleWalletSelect}
      />
    </header>
  );
};

export default Header;
